import React, { useState, useEffect } from "react";
import {
  Card, Row, Col, Typography, Input, Button, Tag, Space,
  Divider, message, Spin, Empty,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, MedicineBoxOutlined, BookOutlined, TagsOutlined, ExperimentOutlined,
} from "@ant-design/icons";
import { doctorSettingsAPI } from "../../services/api";

const { Title, Text } = Typography;

const DoctorSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [specialties, setSpecialties] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [newSpecialty, setNewSpecialty] = useState("");
  const [newQualification, setNewQualification] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [newLabTestCategory, setNewLabTestCategory] = useState("");
  const [labTestCategories, setLabTestCategories] = useState([]);
  const [addingSpec, setAddingSpec] = useState(false);
  const [addingQual, setAddingQual] = useState(false);
  const [addingCat, setAddingCat] = useState(false);
  const [addingLabTestCat, setAddingLabTestCat] = useState(false);
  const [removingItem, setRemovingItem] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await doctorSettingsAPI.getSettings();
      setSpecialties(res.data?.specialties || []);
      setQualifications(res.data?.qualifications || []);
      setCategories(res.data?.categories || []);
      setLabTestCategories(res.data?.labTestCategories || []);
    } catch {
      message.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSpecialty = async () => {
    const name = newSpecialty.trim();
    if (!name) return;
    setAddingSpec(true);
    try {
      const res = await doctorSettingsAPI.addSpecialty(name);
      setSpecialties(res.data?.specialties || []);
      setNewSpecialty("");
      message.success("Specialty added");
    } catch (err) {
      message.error(err.message || "Failed to add specialty");
    } finally {
      setAddingSpec(false);
    }
  };

  const handleRemoveSpecialty = async (name) => {
    setRemovingItem(`spec_${name}`);
    try {
      const res = await doctorSettingsAPI.removeSpecialty(name);
      setSpecialties(res.data?.specialties || []);
      message.success("Specialty removed");
    } catch {
      message.error("Failed to remove");
    } finally {
      setRemovingItem(null);
    }
  };

  const handleAddQualification = async () => {
    const name = newQualification.trim();
    if (!name) return;
    setAddingQual(true);
    try {
      const res = await doctorSettingsAPI.addQualification(name);
      setQualifications(res.data?.qualifications || []);
      setNewQualification("");
      message.success("Qualification added");
    } catch (err) {
      message.error(err.message || "Failed to add qualification");
    } finally {
      setAddingQual(false);
    }
  };

  const handleRemoveQualification = async (name) => {
    setRemovingItem(`qual_${name}`);
    try {
      const res = await doctorSettingsAPI.removeQualification(name);
      setQualifications(res.data?.qualifications || []);
      message.success("Qualification removed");
    } catch {
      message.error("Failed to remove");
    } finally {
      setRemovingItem(null);
    }
  };

  const handleAddCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    setAddingCat(true);
    try {
      const res = await doctorSettingsAPI.addCategory(name);
      setCategories(res.data?.categories || []);
      setNewCategory("");
      message.success("Category added");
    } catch (err) {
      message.error(err.message || "Failed to add category");
    } finally {
      setAddingCat(false);
    }
  };

  const handleRemoveCategory = async (name) => {
    setRemovingItem(`cat_${name}`);
    try {
      const res = await doctorSettingsAPI.removeCategory(name);
      setCategories(res.data?.categories || []);
      message.success("Category removed");
    } catch {
      message.error("Failed to remove");
    } finally {
      setRemovingItem(null);
    }
  };

  const handleAddLabTestCategory = async () => {
    const name = newLabTestCategory.trim();
    if (!name) return;
    setAddingLabTestCat(true);
    try {
      const res = await doctorSettingsAPI.addLabTestCategory(name);
      setLabTestCategories(res.data?.labTestCategories || []);
      setNewLabTestCategory("");
      message.success("Lab test category added");
    } catch (err) {
      message.error(err.message || "Failed to add category");
    } finally {
      setAddingLabTestCat(false);
    }
  };

  const handleRemoveLabTestCategory = async (name) => {
    setRemovingItem(`ltcat_${name}`);
    try {
      const res = await doctorSettingsAPI.removeLabTestCategory(name);
      setLabTestCategories(res.data?.labTestCategories || []);
      message.success("Category removed");
    } catch {
      message.error("Failed to remove");
    } finally {
      setRemovingItem(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Doctor Settings</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        Manage specialties and qualifications available when creating or editing doctors.
      </Text>

      <Row gutter={24}>
        {/* Specialties */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <MedicineBoxOutlined style={{ color: "#1677ff" }} />
                <span>Specialties</span>
                <Tag color="blue">{specialties.length}</Tag>
              </Space>
            }
            style={{ height: "100%" }}
          >
            <Space.Compact style={{ width: "100%", marginBottom: 20 }}>
              <Input
                placeholder="e.g. Cardiology"
                aria-label="Specialty"
                value={newSpecialty}
                onChange={(e) => setNewSpecialty(e.target.value)}
                onPressEnter={handleAddSpecialty}
                size="large"
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddSpecialty}
                loading={addingSpec}
                size="large"
              >
                Add
              </Button>
            </Space.Compact>

            <Divider style={{ margin: "0 0 16px" }} />

            {specialties.length === 0 ? (
              <Empty description="No specialties added yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {specialties.map((spec) => (
                  <Tag
                    key={spec}
                    color="blue"
                    closable
                    closeIcon={
                      removingItem === `spec_${spec}`
                        ? <span style={{ fontSize: 10 }}>...</span>
                        : <DeleteOutlined />
                    }
                    onClose={(event) => { event.preventDefault(); if (!removingItem) handleRemoveSpecialty(spec); }}
                    style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6 }}
                  >
                    {spec}
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        </Col>

        {/* Qualifications */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BookOutlined style={{ color: "#0A6EBD" }} />
                <span>Qualifications</span>
                <Tag color="green">{qualifications.length}</Tag>
              </Space>
            }
            style={{ height: "100%" }}
          >
            <Space.Compact style={{ width: "100%", marginBottom: 20 }}>
              <Input
                placeholder="e.g. MBBS, MD"
                aria-label="Qualification"
                value={newQualification}
                onChange={(e) => setNewQualification(e.target.value)}
                onPressEnter={handleAddQualification}
                size="large"
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddQualification}
                loading={addingQual}
                size="large"
                style={{ backgroundColor: "#0A6EBD", borderColor: "#0A6EBD" }}
              >
                Add
              </Button>
            </Space.Compact>

            <Divider style={{ margin: "0 0 16px" }} />

            {qualifications.length === 0 ? (
              <Empty description="No qualifications added yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {qualifications.map((qual) => (
                  <Tag
                    key={qual}
                    color="green"
                    closable
                    closeIcon={
                      removingItem === `qual_${qual}`
                        ? <span style={{ fontSize: 10 }}>...</span>
                        : <DeleteOutlined />
                    }
                    onClose={(event) => { event.preventDefault(); if (!removingItem) handleRemoveQualification(qual); }}
                    style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6 }}
                  >
                    {qual}
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Categories */}
      <Row gutter={24} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <TagsOutlined style={{ color: "#0A6EBD" }} />
                <span>User Categories</span>
                <Tag color="orange">{categories.length}</Tag>
              </Space>
            }
          >
            <Space.Compact style={{ width: "100%", marginBottom: 20 }}>
              <Input
                placeholder="e.g. Senior Citizen, Corporate"
                aria-label="User category"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onPressEnter={handleAddCategory}
                size="large"
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddCategory}
                loading={addingCat}
                size="large"
                style={{ backgroundColor: "#0A6EBD", borderColor: "#0A6EBD" }}
              >
                Add
              </Button>
            </Space.Compact>

            <Divider style={{ margin: "0 0 16px" }} />

            {categories.length === 0 ? (
              <Empty description="No categories added yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {categories.map((cat) => (
                  <Tag
                    key={cat}
                    color="orange"
                    closable
                    closeIcon={
                      removingItem === `cat_${cat}`
                        ? <span style={{ fontSize: 10 }}>...</span>
                        : <DeleteOutlined />
                    }
                    onClose={(event) => { event.preventDefault(); if (!removingItem) handleRemoveCategory(cat); }}
                    style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6 }}
                  >
                    {cat}
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <ExperimentOutlined style={{ color: "#0A6EBD" }} />
                <span>Lab Test Categories</span>
                <Tag color="cyan">{labTestCategories.length}</Tag>
              </Space>
            }
          >
            <Space.Compact style={{ width: "100%", marginBottom: 20 }}>
              <Input
                placeholder="e.g. Cardiology, Blood Tests"
                aria-label="Lab test category"
                value={newLabTestCategory}
                onChange={(e) => setNewLabTestCategory(e.target.value)}
                onPressEnter={handleAddLabTestCategory}
                size="large"
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddLabTestCategory}
                loading={addingLabTestCat}
                size="large"
                style={{ backgroundColor: "#0A6EBD", borderColor: "#0A6EBD" }}
              >
                Add
              </Button>
            </Space.Compact>

            <Divider style={{ margin: "0 0 16px" }} />

            {labTestCategories.length === 0 ? (
              <Empty description="No lab test categories added yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {labTestCategories.map((cat) => (
                  <Tag
                    key={cat}
                    color="cyan"
                    closable
                    closeIcon={
                      removingItem === `ltcat_${cat}`
                        ? <span style={{ fontSize: 10 }}>...</span>
                        : <DeleteOutlined />
                    }
                    onClose={(event) => { event.preventDefault(); if (!removingItem) handleRemoveLabTestCategory(cat); }}
                    style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6 }}
                  >
                    {cat}
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Referral Points Setting */}
      <Row gutter={24} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <span style={{ fontSize: 18 }}>🎯</span>
                <span>Doctor Referral Points</span>
              </Space>
            }
          >
            <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
              Points awarded to a doctor each time a patient uses their referral code during lab test booking.
            </Text>
            <Text strong>5 points per lab booking with a valid doctor referral code</Text>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DoctorSettingsPage;
